import * as React from 'react';
import { useEffect, useRef, useState } from 'react';

export const HeadingInput = ({
  level,
  value,
  onChange,
}: {
  level: number;
  value: string;
  onChange: any;
}) => {
  const inputRef = useRef<HTMLDivElement>(null);
  const [cursorPosition, setCursorPosition] = useState<number | null>(null);
  const [initialized, setInitialized] = useState(false);

  const styles = {
    h1: { fontSize: '2em', fontWeight: 'bold' },
    h2: { fontSize: '1.5em', fontWeight: 'bold' },
    h3: { fontSize: '1.17em', fontWeight: 'bold' },
    h4: { fontSize: '1em', fontWeight: 'bold' },
    h5: { fontSize: '0.83em', fontWeight: 'bold' },
    h6: { fontSize: '0.67em', fontWeight: 'bold' },
  };

  // Initial content setup and content updates
  useEffect(() => {
    if (inputRef.current) {
      // Only initialize content if it hasn't been initialized yet or if element doesn't have focus
      if (!initialized) {
        inputRef.current.textContent = value || '';
        setInitialized(true);
      } else if (!document.activeElement?.contains(inputRef.current)) {
        // Only update content if it's different from what's already there
        if (inputRef.current.textContent !== value) {
          inputRef.current.textContent = value || '';
        }
      }
    }
  }, [value, level, initialized]);

  // Store selection/cursor position
  const saveSelection = () => {
    const selection = window.getSelection();
    if (
      selection?.rangeCount &&
      inputRef.current?.contains(selection.anchorNode)
    ) {
      const range = selection.getRangeAt(0);
      setCursorPosition(range.startOffset);
    }
  };

  // Restore selection
  const restoreSelection = () => {
    if (cursorPosition !== null && inputRef.current) {
      const selection = window.getSelection();
      if (!selection) return;

      try {
        // Create a new range
        const range = document.createRange();

        // Set range to the proper position in the content
        // Use firstChild if it exists, otherwise use the div itself
        const textNode = inputRef.current.firstChild || inputRef.current;
        const maxOffset = Math.min(
          cursorPosition,
          (inputRef.current.textContent || '').length,
        );

        range.setStart(textNode, maxOffset);
        range.setEnd(textNode, maxOffset);

        // Apply the selection
        selection.removeAllRanges();
        selection.addRange(range);
      } catch (e) {
        console.error('Error restoring selection:', e);
      }
    }
  };

  // Handle input changes
  const handleInput = (e: React.FormEvent<HTMLDivElement>) => {
    saveSelection();
    const newValue = e.currentTarget.textContent || '';
    onChange(newValue);
  };

  // Handle focus
  const handleFocus = () => {
    if (cursorPosition !== null) {
      setTimeout(restoreSelection, 0);
    }
  };

  // After render, restore cursor position if needed
  useEffect(() => {
    if (
      cursorPosition !== null &&
      document.activeElement === inputRef.current
    ) {
      setTimeout(restoreSelection, 0);
    }
  }, [value, cursorPosition]);

  return (
    <div
      ref={inputRef}
      contentEditable
      className="heading-input"
      style={(styles as any)[`h${level}`]}
      onInput={handleInput}
      onFocus={handleFocus}
      onKeyUp={saveSelection}
      onMouseUp={saveSelection}
    />
  );
};
